import type { AccountActionResult, AccountPlan, AccountProfile, AccountPromotionStatus, AppStatus, CurrentAccount, CurrentLegalDocuments, LegalConsentDecision, Playback, PromotionRedeemResult, PublicAlbum, SharedAlbum, Song, UsernameAvailability } from '../types'
import type { DisplayNameUpdatePayload } from './accountProfileScope'
import type { AdminDashboard, AdminEvidenceView, AdminEvidenceViewPayload, AdminMe, AdminOperationAuditPage, AdminOperationReasonPayload, AdminPlanPolicy, AdminPlanPolicyAudit, AdminPlanPolicyUpdatePayload, AdminPolicyPlanCode, AdminPromotion, AdminPromotionCreatePayload, AdminPromotionRedemption, AdminPromotionUpdatePayload, AdminReport, AdminReportUpdatePayload, AdminShareDownloadPolicy, AdminShareDownloadUsage, AdminSong, AdminSongPage, AdminSongQuery, AdminUser, AdminUserPage, AdminUserPlanUpdatePayload, AdminUserQuery } from './adminTypes'
import { adminSongsPath, adminUsersPath } from './adminQuery'

const CSRF_COOKIE_NAME = 'XSRF-TOKEN'
let csrfHeaderName = 'X-XSRF-TOKEN'
let csrfReady = false
let csrfBootstrapPromise: Promise<void> | null = null

const API_ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,63}$/
const API_SUPPORT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{2,63}$/

function normalizeApiErrorCode(value: unknown): string {
  if (typeof value !== 'string') return 'REQUEST_FAILED'
  const normalized = value.trim().toUpperCase()
  return API_ERROR_CODE_PATTERN.test(normalized) ? normalized : 'REQUEST_FAILED'
}

function normalizeApiSupportCode(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toUpperCase()
  return API_SUPPORT_CODE_PATTERN.test(normalized) ? normalized : null
}

function normalizeApiStatus(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 599
    ? value
    : 0
}

export class ApiError extends Error {
  public readonly code: string
  public readonly status: number
  public readonly supportCode: string | null

  /**
   * `backendMessage` is intentionally ignored.
   *
   * Keeping the parameter for compatibility makes accidental construction with
   * a backend natural-language message safe: Error.message is always the
   * normalized machine-readable code.
   */
  constructor(
    code: unknown,
    _backendMessage: unknown,
    status: unknown,
    supportCode: unknown = null,
  ) {
    const normalizedCode = normalizeApiErrorCode(code)
    super(normalizedCode)
    this.name = 'ApiError'
    this.code = normalizedCode
    this.status = normalizeApiStatus(status)
    this.supportCode = normalizeApiSupportCode(supportCode)
  }
}

/**
 * document.cookie에서 지정한 쿠키를 읽습니다.
 *
 * CSRF 토큰 원문은 로그나 오류 메시지로 출력하지 않습니다. 이 함수가 반환한 값은
 * 바로 X-XSRF-TOKEN 헤더에 넣는 용도로만 사용합니다.
 */
function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`
  const item = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix))

  if (!item) return null

  const encodedValue = item.slice(prefix.length)
  try {
    return decodeURIComponent(encodedValue)
  } catch {
    // 브라우저 쿠키가 비정상적으로 인코딩돼 있으면 안전하게 실패 처리합니다.
    return null
  }
}

/**
 * 서버에 CSRF 토큰 발급을 요청하고 XSRF-TOKEN 쿠키가 준비됐는지 확인합니다.
 *
 * 여러 API 요청이 동시에 시작돼도 /api/v1/segno/auth/csrf를 중복 호출하지 않도록 Promise를
 * 공유합니다. forceRefresh=true는 403 이후 토큰을 다시 준비할 때 사용합니다.
 */
async function ensureCsrf(forceRefresh = false): Promise<void> {
  if (forceRefresh) {
    csrfReady = false
    csrfBootstrapPromise = null
  }

  if (csrfReady && readCookie(CSRF_COOKIE_NAME)) return

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = (async () => {
      const response = await fetch('/api/v1/segno/auth/csrf', {
        credentials: 'include',
        cache: 'no-store',
      })

      if (!response.ok) {
        throw new ApiError(
          'CSRF_BOOTSTRAP_FAILED',
          '보안 토큰을 준비하지 못했습니다.',
          response.status,
        )
      }

      const data = (await response.json()) as { headerName?: string }
      if (data.headerName) csrfHeaderName = data.headerName

      // Spring Security SPA 표준에서는 쿠키의 원본 토큰을 요청 헤더로 보냅니다.
      // 응답 본문의 마스킹 토큰을 장기간 캐시하지 않습니다.
      if (!readCookie(CSRF_COOKIE_NAME)) {
        throw new ApiError(
          'CSRF_COOKIE_MISSING',
          '보안 쿠키를 준비하지 못했습니다.',
          0,
        )
      }

      csrfReady = true
    })().finally(() => {
      csrfBootstrapPromise = null
    })
  }

  await csrfBootstrapPromise
}

function invalidateCsrf(): void {
  csrfReady = false
  csrfBootstrapPromise = null
}

function isReplayableBody(body: BodyInit | null | undefined): boolean {
  // 현재 mioSegno mutation은 JSON 문자열 또는 본문 없음으로 구성됩니다.
  // ReadableStream처럼 한 번 소비하면 재사용할 수 없는 본문은 자동 재시도하지 않습니다.
  return body == null || typeof body === 'string'
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  mutation = false,
  csrfRetryAllowed = true,
): Promise<T> {
  const headers = new Headers(init.headers)

  if (mutation) {
    await ensureCsrf()
    const token = readCookie(CSRF_COOKIE_NAME)
    if (!token) {
      invalidateCsrf()
      throw new ApiError(
        'CSRF_COOKIE_MISSING',
        '보안 쿠키가 없습니다. 다시 시도해주세요.',
        0,
      )
    }
    headers.set(csrfHeaderName, token)
  }

  if (init.body && !(init.body instanceof Blob) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (response.status === 204) {
    return undefined as T
  }

  const body = await response.json().catch(() => ({}))

  if (!response.ok) {
    const error = body?.error
    const code = normalizeApiErrorCode(error?.code)

    // CSRF 쿠키는 다른 탭, 로그인·로그아웃, 백엔드 재시작 등으로 바뀔 수 있습니다.
    // 서버가 명시적으로 CSRF 오류를 반환했을 때만 새 토큰을 받아 한 번 재시도합니다.
    // 무한 재시도와 업무 API의 중복 실행을 막기 위해 최대 한 번만 수행합니다.
    const csrfRejected = response.status === 403
      && (code === 'CSRF_INVALID' || code === 'CSRF_MISSING')

    if (
      mutation
      && csrfRetryAllowed
      && csrfRejected
      && isReplayableBody(init.body)
    ) {
      invalidateCsrf()
      await ensureCsrf(true)
      return request<T>(path, init, mutation, false)
    }

    // Backend natural-language text is not a frontend contract.
    // Keep Error.message machine-readable too, so accidental `.message` rendering
    // cannot leak a backend-localized sentence into the UI.
    throw new ApiError(
      code,
      code,
      response.status,
      normalizeApiSupportCode(error?.supportCode),
    )
  }

  return body as T
}

async function requestAndRefreshCsrf<T>(
  path: string,
  init: RequestInit,
): Promise<T> {
  const result = await request<T>(path, init, true)
  // 인증 상태가 바뀌는 작업 뒤에는 Spring Security가 기존 CSRF 쿠키를 지울 수 있으므로
  // 다음 mutation에서 새 토큰을 준비하도록 캐시 상태를 무효화합니다.
  invalidateCsrf()
  return result
}

export const api = {
  status: () => request<AppStatus>('/api/v1/segno/app/status'),

  currentLegalDocuments: (locale: string) =>
    request<CurrentLegalDocuments>(`/api/v1/segno/legal/current?${new URLSearchParams({ locale }).toString()}`),

  usernameAvailability: (username: string) =>
    request<UsernameAvailability>(`/api/v1/segno/auth/username-availability?${new URLSearchParams({ username }).toString()}`),

  login: (username: string, password: string) =>
    requestAndRefreshCsrf<CurrentAccount>('/api/v1/segno/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  register: (payload: Record<string, unknown>) =>
    requestAndRefreshCsrf<CurrentAccount>('/api/v1/segno/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  serviceEnrollment: (payload: {
    username: string
    password: string
    legalLocale: string
    legalConsents: LegalConsentDecision[]
  }) =>
    requestAndRefreshCsrf<CurrentAccount>('/api/v1/segno/auth/service-enrollment', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  requestUsernameReminder: (recoveryEmail: string) =>
    request<{ accepted: boolean }>('/api/v1/segno/auth/recovery/username/request', {
      method: 'POST',
      body: JSON.stringify({ recoveryEmail }),
    }, true),

  requestPasswordReset: (username: string, recoveryEmail: string) =>
    request<{ accepted: boolean }>('/api/v1/segno/auth/recovery/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ username, recoveryEmail }),
    }, true),

  confirmPasswordReset: (token: string, newPassword: string, newPasswordConfirm: string) =>
    request<{ completed: boolean }>('/api/v1/segno/auth/recovery/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword, newPasswordConfirm }),
    }, true),

  cancelPasswordReset: (userId: number, token: string) =>
    request<{ completed: boolean }>('/api/v1/segno/auth/recovery/password-reset/cancel', {
      method: 'POST',
      body: JSON.stringify({ userId, token }),
    }, true),

  logout: () =>
    requestAndRefreshCsrf('/api/v1/segno/auth/logout', { method: 'POST' }),

  me: () => request<CurrentAccount>('/api/v1/segno/auth/me'),

  accountProfile: () => request<AccountProfile>('/api/v1/segno/account/profile'),

  accountPlan: () => request<AccountPlan>('/api/v1/segno/account/plan'),

  accountPromotion: () =>
    request<AccountPromotionStatus>('/api/v1/segno/account/plan/promotion'),

  redeemPromotion: (code: string) =>
    request<PromotionRedeemResult>('/api/v1/segno/account/plan/promotion/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }, true),

  updateAccountProfile: (payload: DisplayNameUpdatePayload) => request<AccountProfile>('/api/v1/segno/account/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, true),

  changeAccountPassword: (payload: {
    currentPassword: string
    newPassword: string
    newPasswordConfirm: string
  }) => requestAndRefreshCsrf<AccountActionResult>('/api/v1/segno/account/password', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

  updateRecoveryEmail: (currentPassword: string, recoveryEmail: string) =>
    request<AccountProfile>('/api/v1/segno/account/recovery-email', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, recoveryEmail }),
    }, true),

  requestRecoveryEmailVerification: () =>
    request<AccountActionResult>('/api/v1/segno/account/recovery-email/verification/request', {
      method: 'POST',
    }, true),

  confirmRecoveryEmailVerification: (token: string) =>
    request<AccountActionResult>('/api/v1/segno/account/recovery-email/verification/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }, true),

  cancelRecoveryEmailVerification: (userId: number, token: string) =>
    request<AccountActionResult>('/api/v1/segno/account/recovery-email/verification/cancel', {
      method: 'POST',
      body: JSON.stringify({ userId, token }),
    }, true),

  getSharedSong: (shareId: string) =>
    request<Song>(`/api/v1/segno/songs/share/${encodeURIComponent(shareId)}`),

  getPlayback: (shareId: string) =>
    request<Playback>(
      `/api/v1/segno/songs/share/${encodeURIComponent(shareId)}/play-url`,
      { method: 'POST' },
      true,
    ),

  getSharedDownload: (shareId: string) =>
    request<Playback>(
      `/api/v1/segno/songs/share/${encodeURIComponent(shareId)}/download-url`,
      { method: 'POST' },
      true,
    ),

  listSongs: () => request<Song[]>('/api/v1/segno/songs'),

  getSong: (id: number) => request<Song>(`/api/v1/segno/songs/${id}`),

  getOwnerPlayback: (id: number) =>
    request<Playback>(
      `/api/v1/segno/songs/${id}/play-url`,
      { method: 'POST' },
      true,
    ),

  createSong: (payload: Record<string, unknown>) =>
    request<Song>('/api/v1/segno/songs', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  updateSong: (id: number, payload: Record<string, unknown>) =>
    request<Song>(`/api/v1/segno/songs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, true),

  deleteSong: (id: number) =>
    request<void>(`/api/v1/segno/songs/${id}`, { method: 'DELETE' }, true),

  activateSongShare: (
    id: number,
    rightsConfirmed: boolean,
    requestedExpiresAt?: string | null,
  ) =>
    request<Song>(`/api/v1/segno/songs/${id}/share`, {
      method: 'POST',
      body: JSON.stringify({
        rightsConfirmed,
        requestedExpiresAt: requestedExpiresAt ?? null,
      }),
    }, true),

  deactivateSongShare: (id: number) =>
    request<Song>(`/api/v1/segno/songs/${id}/share`, { method: 'DELETE' }, true),

  listAlbums: () => request<SharedAlbum[]>('/api/v1/segno/albums'),

  createAlbum: (payload: { title: string; songIds: number[] }) =>
    request<SharedAlbum>('/api/v1/segno/albums', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  updateAlbum: (id: number, payload: { title: string; songIds: number[] }) =>
    request<SharedAlbum>(`/api/v1/segno/albums/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, true),

  activateAlbumShare: (id: number, requestedExpiresAt?: string | null) =>
    request<SharedAlbum>(`/api/v1/segno/albums/${id}/share`, {
      method: 'POST',
      body: JSON.stringify({ requestedExpiresAt: requestedExpiresAt ?? null }),
    }, true),

  deactivateAlbum: (id: number) =>
    request<void>(`/api/v1/segno/albums/${id}`, { method: 'DELETE' }, true),

  deleteAlbum: (id: number) =>
    request<void>(`/api/v1/segno/albums/${id}/library`, { method: 'DELETE' }, true),

  addSongToAlbum: (albumId: number, songId: number) =>
    request<SharedAlbum>(`/api/v1/segno/albums/${albumId}/songs/${songId}`, { method: 'POST' }, true),

  removeSongFromAlbum: (albumId: number, songId: number) =>
    request<SharedAlbum>(`/api/v1/segno/albums/${albumId}/songs/${songId}`, { method: 'DELETE' }, true),

  moveSongBetweenAlbums: (fromAlbumId: number, songId: number, toAlbumId: number) =>
    request<SharedAlbum>(`/api/v1/segno/albums/${fromAlbumId}/songs/${songId}/move/${toAlbumId}`, { method: 'POST' }, true),

  getSharedAlbum: (shareToken: string) =>
    request<PublicAlbum>(`/api/v1/segno/albums/share/${encodeURIComponent(shareToken)}`),

  prepareUpload: (payload: Record<string, unknown>) =>
    request<{
      uploadId: string
      fileId: string
      uploadUrl: string
      method: 'PUT'
      maxBytes: number
      requiredHeaders: Record<string, string>
    }>('/api/v1/segno/media/uploads/prepare', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  completeUpload: (uploadId: string) =>
    request<{
      fileId: string
      status: string
      durationSeconds?: number
    }>(`/api/v1/segno/media/uploads/${encodeURIComponent(uploadId)}/complete`, {
      method: 'POST',
    }, true),

  mediaPlayUrl: (fileId: string) =>
    request<{ fileId: string; downloadUrl: string; expiresAt?: string }>(
      `/api/v1/segno/media/files/${encodeURIComponent(fileId)}/play-url`,
      { method: 'POST' },
      true,
    ),

  discardMediaFile: (fileId: string) =>
    request<void>(`/api/v1/segno/media/files/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
    }, true),

  report: (payload: { shareId: string; reporterName: string; reporterEmail: string; rightsDescription: string; infringementDescription: string; claimantEvidence: string; additionalNotes?: string }) =>
    request('/api/v1/segno/reports', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminMe: () => request<AdminMe>('/api/v1/segno/admin/me'),

  adminDashboard: () => request<AdminDashboard>('/api/v1/segno/admin/dashboard'),

  adminUsers: (options: AdminUserQuery) =>
    request<AdminUserPage>(adminUsersPath(options)),

  adminSongs: (options: AdminSongQuery) =>
    request<AdminSongPage>(adminSongsPath(options)),

  adminReports: () => request<AdminReport[]>('/api/v1/segno/admin/reports'),

  adminViewSongEvidence: (songId: number, payload: AdminEvidenceViewPayload) =>
    request<AdminEvidenceView>(`/api/v1/segno/admin/songs/${songId}/evidence/view`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminStopSongShare: (songId: number, payload: AdminOperationReasonPayload) =>
    request<AdminSong>(`/api/v1/segno/admin/songs/${songId}/share/stop`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminRestoreSongShare: (songId: number, payload: AdminOperationReasonPayload) =>
    request<AdminSong>(`/api/v1/segno/admin/songs/${songId}/share/restore`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminBlindSong: (songId: number, payload: AdminOperationReasonPayload) =>
    request<AdminSong>(`/api/v1/segno/admin/songs/${songId}/blind`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminUnblindSong: (songId: number, payload: AdminOperationReasonPayload) =>
    request<AdminSong>(`/api/v1/segno/admin/songs/${songId}/unblind`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminAudits: (page = 0, size = 50) =>
    request<AdminOperationAuditPage>(`/api/v1/segno/admin/audits?${new URLSearchParams({
      page: String(page),
      size: String(size),
    }).toString()}`),

  adminPolicies: () => request<AdminPlanPolicy[]>('/api/v1/segno/admin/policies'),

  adminShareDownloadPolicy: () =>
    request<AdminShareDownloadPolicy>('/api/v1/segno/admin/share-download-policy'),

  adminUpdateShareDownloadPolicy: (maxDownloads: number) =>
    request<AdminShareDownloadPolicy>('/api/v1/segno/admin/share-download-policy', {
      method: 'PUT',
      body: JSON.stringify({ maxDownloads }),
    }, true),

  adminShareDownloadUsage: (songIds: number[]) => {
    if (songIds.length === 0) return Promise.resolve([] as AdminShareDownloadUsage[])
    const params = new URLSearchParams()
    for (const id of songIds) params.append('songIds', String(id))
    return request<AdminShareDownloadUsage[]>(`/api/v1/segno/admin/share-download-usage?${params.toString()}`)
  },

  adminUpdatePolicy: (planCode: AdminPolicyPlanCode, payload: AdminPlanPolicyUpdatePayload) =>
    request<AdminPlanPolicy>(`/api/v1/segno/admin/policies/${encodeURIComponent(planCode)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, true),

  adminPolicyAudits: (planCode: AdminPolicyPlanCode) =>
    request<AdminPlanPolicyAudit[]>(`/api/v1/segno/admin/policies/${encodeURIComponent(planCode)}/audits`),

  adminPromotions: () =>
    request<AdminPromotion[]>('/api/v1/segno/admin/promotions'),

  adminCreatePromotion: (payload: AdminPromotionCreatePayload) =>
    request<AdminPromotion>('/api/v1/segno/admin/promotions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true),

  adminUpdatePromotion: (promotionId: number, payload: AdminPromotionUpdatePayload) =>
    request<AdminPromotion>(`/api/v1/segno/admin/promotions/${promotionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, true),

  adminSetPromotionActive: (promotionId: number, active: boolean) =>
    request<AdminPromotion>(`/api/v1/segno/admin/promotions/${promotionId}/active`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    }, true),

  adminPromotionRedemptions: (promotionId: number) =>
    request<AdminPromotionRedemption[]>(`/api/v1/segno/admin/promotions/${promotionId}/redemptions`),

  adminUpdatePlan: (centralUserId: string, payload: AdminUserPlanUpdatePayload) =>
    request<AdminUser>(`/api/v1/segno/admin/users/${encodeURIComponent(centralUserId)}/plan`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, true),

  adminUpdateReport: (reportId: number, payload: AdminReportUpdatePayload) =>
    request<AdminReport>(`/api/v1/segno/admin/reports/${reportId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, true),

  practiceTelemetryStart: (shareId: string) =>
    request<{ sessionId: string }>('/api/v1/segno/practice-telemetry/start', {
      method: 'POST',
      body: JSON.stringify({ shareId }),
    }, true),

  practiceTelemetryHeartbeat: (sessionId: string) =>
    request<void>(`/api/v1/segno/practice-telemetry/${encodeURIComponent(sessionId)}/heartbeat`, {
      method: 'POST',
    }, true),

  practiceTelemetryStop: (sessionId: string) =>
    request<void>(`/api/v1/segno/practice-telemetry/${encodeURIComponent(sessionId)}/stop`, {
      method: 'POST',
    }, true),
}
